import { CustomerJson } from "@redotech/redo-model/customer";
import { ICustomerActivity } from "@redotech/redo-model/customer-activity/customer-activity-definition";
import {
  CustomerActivityFilterOptions,
  customerEventFilterOptionsToQueryParams,
} from "@redotech/redo-model/customer-activity/customer-activity-filters";
import {
  CustomerEventSchema,
  NonDiscriminatedCustomerEventSchema,
} from "@redotech/redo-model/customer-event/customer-event-zod-schema";
import { RedoMerchantClient } from "../client";

export interface CustomerMetrics {
  totalOrders: number;
  totalOrderValue: number;
  averageOrderValue: number;
  totalReturns: number;
  processedReturns: number;
  totalReturnValue: number;
  returnRate: number;
  totalClaims: number;
  paidClaimValue: number;
  claimRate: number;
}

/**
 * GET /customers/by-email/:email
 */
export async function getCustomerByEmail(
  client: RedoMerchantClient,
  { email, signal }: { email: string; signal?: AbortSignal },
): Promise<CustomerJson> {
  const response = await client.client.get(
    `customers/by-email/${encodeURIComponent(email)}`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /customers/:id
 */
export async function getCustomer(
  client: RedoMerchantClient,
  { id, signal }: { id: string; signal?: AbortSignal },
): Promise<CustomerJson> {
  const response = await client.client.get(
    `customers/${encodeURIComponent(id)}`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /customers/:id/metrics
 */
export async function getCustomerMetrics(
  client: RedoMerchantClient,
  { id, signal }: { id: string; signal?: AbortSignal },
): Promise<CustomerMetrics> {
  const response = await client.client.get(
    `customers/${encodeURIComponent(id)}/metrics`,
    { headers: client.authorization(), signal },
  );
  return response.data;
}

/**
 * GET /customers/:id/events
 */
export async function getCustomerEvents(
  client: RedoMerchantClient,
  {
    customerId,
    filterOptions,
    signal,
  }: {
    customerId: string;
    filterOptions?: CustomerActivityFilterOptions;
    signal?: AbortSignal;
  },
): Promise<ICustomerActivity> {
  const maybeQueryParamsString =
    customerEventFilterOptionsToQueryParams(filterOptions);
  const queryParamsUri = maybeQueryParamsString
    ? `?${maybeQueryParamsString}`
    : "";

  const response = await client.client.get(
    `customers/${encodeURIComponent(customerId)}/events${queryParamsUri}`,
    { headers: client.authorization(), signal },
  );

  try {
    const events = response.data.customerEvents;
    const validEvents = [];
    for (const event of events) {
      let parsedEvent = CustomerEventSchema.safeParse(event);
      if (parsedEvent.success) {
        validEvents.push(parsedEvent.data);
        continue;
      }

      // TODO: Remove this secondary parse in a couple weeks if we haven't seen the error log
      parsedEvent = NonDiscriminatedCustomerEventSchema.safeParse(event);
      if (parsedEvent.success) {
        validEvents.push(parsedEvent.data);
        console.warn(
          "Discriminated CustomerEventSchema failed where non-discriminated succeeded",
          { eventId: event._id, eventType: event.eventType },
        );
        continue;
      } else {
        console.error("Invalid event found for team", event);
      }
    }
    return { customerEvents: validEvents };
  } catch (error) {
    console.error(error);
    return { customerEvents: [] };
  }
}
